import { Dialog, Transition } from '@headlessui/react';
import { Field, Form, Formik } from 'formik';
import { Fragment, useEffect } from 'react';
import * as Yup from 'yup';

const PLACEHOLDER_DESCRIPTIONS: Record<string, string> = {
  '{{VerificationLink}}': 'Email verification URL',
  '{{TwoFactorCode}}': 'One-time authentication code',
  '{{PasswordResetLink}}': 'Password reset URL',
  '{{InvitationLink}}': 'Invitation acceptance URL',
  '{{InviterName}}': 'Name of the inviting user',
  '{{UserName}}': 'Recipient display name',
  '{{UserEmail}}': 'Recipient email address',
  '{{TenantName}}': 'Tenant name',
  '{{CompanyName}}': 'Active company name',
};

import {
  closeTemplateEditor,
  fetchWorkspaceEmailTemplateDetail,
  previewWorkspaceEmailTemplateThunk,
  saveWorkspaceEmailTemplate,
} from '../../store/workspaceEmailTemplatesSlice';

import { useAppDispatch, useAppSelector } from '@store/hooks';

interface Props {
  tenantId: string;
  onSaved: () => void;
}

const schema = Yup.object({
  body: Yup.string().required('Template body is required.'),
});

const EmailTemplateEditorModal: React.FC<Props> = ({ tenantId, onSaved }) => {
  const dispatch = useAppDispatch();
  const {
    open,
    templateType,
    detail,
    detailStatus,
    detailError,
    saveStatus,
    saveError,
    previewStatus,
    previewError,
    previewResult,
  } = useAppSelector((s) => s.settingsWorkspaceEmailTemplates.editor);

  const templateList = useAppSelector(
    (s) => s.settingsWorkspaceEmailTemplates.list
  );

  const selectedListItem = templateList.find(
    (item) => item.templateType === templateType
  );

  useEffect(() => {
    if (open && templateType) {
      dispatch(fetchWorkspaceEmailTemplateDetail({ tenantId, templateType }));
    }
  }, [dispatch, open, tenantId, templateType]);

  const handleClose = () => {
    dispatch(closeTemplateEditor());
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-4xl bg-white rounded-lg shadow-xl p-6">
              <Dialog.Title className="text-base font-semibold text-gray-900 mb-4">
                Edit Template{' '}
                {selectedListItem ? `— ${selectedListItem.displayName}` : ''}
              </Dialog.Title>

              {detailStatus === 'loading' && (
                <div className="py-6 text-sm text-gray-500">
                  Loading template...
                </div>
              )}

              {detailStatus === 'failed' && (
                <div className="py-4 text-sm text-red-600">{detailError}</div>
              )}

              {detailStatus !== 'loading' &&
                detailStatus !== 'failed' &&
                templateType && (
                  <Formik
                    initialValues={{ body: detail?.body ?? '' }}
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={async (values, { setSubmitting }) => {
                      try {
                        await dispatch(
                          saveWorkspaceEmailTemplate({
                            tenantId,
                            templateType,
                            body: values.body,
                          })
                        ).unwrap();

                        onSaved();
                        dispatch(closeTemplateEditor());
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({ errors, touched, values }) => (
                      <Form className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                          <div className="lg:col-span-2">
                            <label
                              htmlFor="body"
                              className="block text-sm font-medium text-gray-700 mb-1"
                            >
                              Template Body
                            </label>
                            <Field
                              as="textarea"
                              id="body"
                              name="body"
                              rows={14}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            {errors.body && touched.body && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.body}
                              </p>
                            )}
                            {saveError && (
                              <p className="mt-2 text-sm text-red-600">
                                {saveError}
                              </p>
                            )}
                          </div>

                          <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                            <h4 className="text-sm font-semibold text-gray-900">
                              Placeholders
                            </h4>
                            {selectedListItem && (
                              <div className="mt-2 overflow-x-auto">
                                <table className="w-full text-xs text-left">
                                  <thead>
                                    <tr className="border-b border-gray-200">
                                      <th className="pb-1 pr-2 font-medium text-gray-500">
                                        Token
                                      </th>
                                      <th className="pb-1 font-medium text-gray-500">
                                        Description
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedListItem.availablePlaceholders.map(
                                      (p) => {
                                        const isRequired =
                                          p ===
                                          selectedListItem.requiredPlaceholder;
                                        return (
                                          <tr
                                            key={p}
                                            className="border-b border-gray-100 last:border-0"
                                          >
                                            <td className="py-1 pr-2 align-top">
                                              <code
                                                className={`px-1 py-0.5 rounded font-mono ${
                                                  isRequired
                                                    ? 'text-red-700 bg-red-50'
                                                    : 'text-gray-700 bg-gray-100'
                                                }`}
                                              >
                                                {p}
                                              </code>
                                            </td>
                                            <td className="py-1 text-gray-600 align-top">
                                              {PLACEHOLDER_DESCRIPTIONS[p] ??
                                                ''}
                                              {isRequired && (
                                                <span className="ml-1 text-red-500 font-medium">
                                                  required
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      }
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (!templateType) return;
                              dispatch(
                                previewWorkspaceEmailTemplateThunk({
                                  templateType,
                                  body: values.body,
                                })
                              );
                            }}
                            disabled={previewStatus === 'loading'}
                            className="px-4 py-2 text-sm font-medium text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-50 disabled:opacity-50"
                          >
                            {previewStatus === 'loading'
                              ? 'Previewing…'
                              : 'Preview'}
                          </button>
                        </div>

                        {previewStatus === 'failed' && previewError && (
                          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {previewError}
                          </div>
                        )}

                        {previewResult && previewStatus === 'succeeded' && (
                          <div className="rounded-md border border-gray-200 bg-white">
                            <div className="border-b border-gray-200 px-3 py-2 text-sm font-medium text-gray-700">
                              Preview
                            </div>
                            <iframe
                              className="w-full h-96 border-0"
                              srcDoc={previewResult.rendered}
                              sandbox="allow-same-origin"
                              title="Email preview"
                            />

                            {previewResult.warnings.length > 0 && (
                              <div className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                Unrecognized placeholders:{' '}
                                {previewResult.warnings.join(', ')}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="flex justify-end gap-3 pt-2">
                          <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={saveStatus === 'loading'}
                            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                          >
                            {saveStatus === 'loading' ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EmailTemplateEditorModal;
